import React, { useState, useEffect } from 'react';
import { 
  Lock,
  Save,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Shield,
  KeyRound
} from 'lucide-react';
import purchaseRequestService from '@/services/purchaseRequestService';
import { useAuth } from '@/contexts/AuthContext';

const GMDPin = () => {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPins, setShowPins] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.UserType?.name === 'super_admin' || user?.Role?.name === 'super_admin';

  useEffect(() => {
    checkPinStatus();
  }, []);

  const checkPinStatus = async () => {
    try {
      const response = await purchaseRequestService.checkGMDPin();
      if (response.data.success) {
        setHasPin(response.data.data.hasPin);
      }
    } catch (error) {
      console.error('Error checking PIN status:', error);
    }
  };

  const handleSetPin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (hasPin && !currentPin) {
      setError('Current PIN is required');
      return;
    }

    if (!newPin || newPin.length !== 4) {
      setError('PIN must be exactly 4 digits');
      return;
    }

    if (!/^\d{4}$/.test(newPin)) {
      setError('PIN must contain only numbers');
      return;
    }

    if (newPin !== confirmPin) {
      setError('PINs do not match');
      return;
    }

    try {
      setLoading(true);
      const response = await purchaseRequestService.setGMDPin({
        currentPin: hasPin ? currentPin : null,
        newPin
      });

      if (response.data.success) {
        setSuccess(hasPin ? 'PIN updated successfully!' : 'PIN set successfully!');
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
        setHasPin(true);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to set PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!resetPassword) {
      setError('Enter your password to reset the PIN');
      return;
    }

    try {
      setResetting(true);
      const response = await purchaseRequestService.resetGMDPin({ password: resetPassword });
      if (response.data.success) {
        setSuccess(response.data.message);
        setHasPin(false);
        setShowReset(false);
        setResetPassword('');
        setCurrentPin('');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reset PIN');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Security PIN</h1>
        <p className="text-gray-600 mt-1">Set or change your 4-digit approval PIN</p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
            <span className="text-green-800">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center mb-6">
          <Shield className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">
            {hasPin ? 'Change PIN' : 'Set PIN'}
          </h3>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Security Notice:</strong> Your PIN will be required to approve high-value purchase requests. 
            Keep it secure and do not share it with anyone.
          </p>
        </div>

        <form onSubmit={handleSetPin} className="space-y-6">
          {hasPin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current PIN
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  style={showPins ? undefined : { WebkitTextSecurity: 'disc' }}
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  maxLength={4}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                  placeholder="••••"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New PIN
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                style={showPins ? undefined : { WebkitTextSecurity: 'disc' }}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                maxLength={4}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                placeholder="••••"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Enter a 4-digit PIN (numbers only)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New PIN
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                style={showPins ? undefined : { WebkitTextSecurity: 'disc' }}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                maxLength={4}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                placeholder="••••"
              />
            </div>
          </div>

          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setShowPins(!showPins)}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              {showPins ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide PINs
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Show PINs
                </>
              )}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {hasPin ? 'Updating PIN...' : 'Setting PIN...'}
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                {hasPin ? 'Update PIN' : 'Set PIN'}
              </>
            )}
          </button>
        </form>
      </div>

      {isSuperAdmin && hasPin && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-2">
            <KeyRound className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Forgot PIN?</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Reset clears the current PIN so the GMD can set a new one. The GMD is notified and the reset is recorded.
          </p>

          {!showReset ? (
            <button
              type="button"
              onClick={() => setShowReset(true)}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors font-medium"
            >
              Reset GMD PIN
            </button>
          ) : (
            <form onSubmit={handleResetPin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your password
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={resetting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-medium"
                >
                  {resetting ? 'Resetting...' : 'Confirm reset'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowReset(false);
                    setResetPassword('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default GMDPin;
